import { create } from 'zustand';
import { widgetsApi } from '../../../lib/apiClient';
import { toast } from '../../../lib/toast';

export interface Widget {
  id: string;
  name: string;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  config?: Record<string, any>;
}

interface WidgetsState {
  widgets: Widget[];
  loading: boolean;
  fetchWidgets: (options?: { silent?: boolean }) => Promise<void>;
  toggleWidget: (id: string) => Promise<void>;
  deleteWidget: (id: string) => Promise<void>;
  createWidget: (data: { name: string; config: Record<string, any> }) => Promise<Widget>;
  updateWidget: (id: string, data: { name?: string; isActive?: boolean; config?: Record<string, any> }) => Promise<Widget>;
  duplicateWidget: (id: string, name: string) => Promise<Widget>;
}

export const useWidgetsStore = create<WidgetsState>((set, get) => ({
  widgets: [],
  loading: false,

  fetchWidgets: async (options?: { silent?: boolean }) => {
    set({ loading: true });
    try {
      const data = await widgetsApi.list();
      set({ widgets: data });
    } catch (err: any) {
      if (!options?.silent) {
        toast.error('Failed to load widgets: ' + (err?.message || err));
      }
    } finally {
      set({ loading: false });
    }
  },

  toggleWidget: async (id: string) => {
    const widget = get().widgets.find(w => w.id === id);
    if (!widget) return;
    try {
      await widgetsApi.toggle(id);
      set(state => ({
        widgets: state.widgets.map(w => w.id === id ? { ...w, isActive: !w.isActive } : w),
      }));
      toast.success(`Widget "${widget.name}" ${widget.isActive ? 'disabled' : 'enabled'}`);
    } catch (err: any) {
      toast.error('Failed to toggle widget: ' + (err?.message || err));
    }
  },

  deleteWidget: async (id: string) => {
    const widget = get().widgets.find(w => w.id === id);
    if (!widget) return;
    try {
      await widgetsApi.delete(id);
      set(state => ({ widgets: state.widgets.filter(w => w.id !== id) }));
      toast.success(`Widget "${widget.name}" deleted`);
    } catch (err: any) {
      toast.error('Failed to delete widget: ' + (err?.message || err));
    }
  },

  createWidget: async (data) => {
    const widget = await widgetsApi.create(data);
    set(state => ({ widgets: [...state.widgets, widget] }));
    return widget;
  },

  updateWidget: async (id, data) => {
    const widget = await widgetsApi.update(id, data);
    set(state => ({
      widgets: state.widgets.map(w => w.id === id ? { ...w, ...widget } : w),
    }));
    return widget;
  },

  duplicateWidget: async (id, name) => {
    const widget = await widgetsApi.duplicate(id, name);
    set(state => ({ widgets: [...state.widgets, widget] }));
    return widget;
  },
}));
